import React, { useContext, useState } from 'react';

import {FieldGroup, InfiniteScroll} from '../../components';
import { AjaxForm } from '../../forms';
import { FormHelper, ValidationHelper } from '../../helpers';

import ProductListItem from './ListItem';
import QuickAddProduct from '../../product/components/QuickAddProduct';

const openNewProductTab = (productId)=>{
  return window.open(`product?preview=${productId}`, "_blank")
}

const ProductList = ({
  list = 'default',
  query = '',
  filters = {},
  firstPageOnly = false,
  onActionButtonClick,
  // When provided, the action button on each card switches from "View" (opens
  // preview popup) to "Select" — clicking it calls onSelectProduct(product)
  // directly, equivalent to clicking ORDER/SELL inside the preview popup.
  // Used by V2SearchProduct on the request flow's SEARCH PRODUCT → Database tab.
  onSelectProduct,
  limit,
  onResultsLoaded,
  requestType,
  // DOM id of the ancestor that owns the page's scroll (e.g. the V2 request
  // flow's middle pane is `.rc-request-v2__main` with id="rc-request-v2-scroll").
  // Without this, react-infinite-scroll-component listens on window scroll,
  // which never fires when the page's scroll lives in an inner element.
  scrollableTarget,
  children,
}) => {
  const fetchParams = {
    list,
    query,
    filters,
  };

  // Bail out only when there is genuinely nothing to search on. category_id is
  // set by ProductChoice's brand-change effect, so it stands in for "a brand was
  // picked" -- which used to be the only way to reach the results list. A
  // reference-only search (typed next to Select Brand, no brand chosen) leaves
  // category_id empty, so check the reference too or the list silently renders
  // nothing even though the API would have matched.
  // What the rep already typed, so the inline add can seed itself.
  const quickAddPrefill = {
    brand_id: filters?.brand_id,
    reference_number: filters?.reference_number,
    query,
  };

  // Nothing to search on yet. Still offer the inline add (quietly), so a
  // rep who already knows the product is missing does not have to run a
  // search they expect to fail first.
  if (!query && !filters.category_id && !filters.reference_number) {
    return (
      <QuickAddProduct
        variant="quiet"
        prefill={quickAddPrefill}
        onCreated={(product) => onSelectProduct ? onSelectProduct(product) : onActionButtonClick?.(product)}
      />
    );
  }

  return (
    <InfiniteScroll
      fetchUrl='/api/products/search'
      fetchParams={fetchParams}
      firstPageOnly={firstPageOnly}
      emptyMessage="No products found."
      onResultsLoaded={onResultsLoaded}
      fetchLimit={limit || 8}
      scrollableTarget={scrollableTarget}
    >
      {({ items, isLoading, hasMore }) => (
        <>
          {items.length > 0 && (
            <div className="pcard-grid">
              {items.map((product) => (
                <ProductListItem
                  onActionButtonClick={
                    onSelectProduct
                      ? () => onSelectProduct(product)
                      : () => openNewProductTab(product.id)
                  }
                  actionButtonLabel={onSelectProduct ? "Select" : "View"}
                  actionAsButton={!!onSelectProduct}
                  key={product.id}
                  product={product}
                  requestType={requestType}
                />
              ))}
            </div>
          )}

          {!isLoading && items.length && !hasMore && (
              children
          )}

          {/* Reachable even when the search returned near-misses rather
              than nothing. */}
          {!isLoading && items.length > 0 && (
            <QuickAddProduct
              variant="quiet"
              prefill={quickAddPrefill}
              onCreated={(product) => onSelectProduct ? onSelectProduct(product) : onActionButtonClick?.(product)}
            />
          )}

          { !isLoading && items.length === 0 && (
            <div className="product-not-found">
              <hr/>

              <h2>Item not found</h2>

              <AjaxForm
                submitLabel='Save product'
                submitAction={({ url }) => onActionButtonClick(null, url)}
                initialValues={FormHelper.initialValues(['url'])}
                validationSchema={ValidationHelper.schema(['url'])}
              >
                <FieldGroup
                  placeholder="Copy paste a URL link from a website"
                  name={'url'}
                />
              </AjaxForm>

              {/* Inline catalogue add — only rendered while the platform
                  auto-approve gate is ON (the component self-hides), since
                  a pending row could not be selected afterwards. */}
              <QuickAddProduct
                prefill={quickAddPrefill}
                onCreated={(product) => onSelectProduct ? onSelectProduct(product) : onActionButtonClick?.(product)}
              />
            </div>
          )}
        </>
      )}
    </InfiniteScroll>
  );
}

export default ProductList;
